import React, { useEffect, useRef, useState } from 'react';
import { shipmentService } from '../../api/shipmentService';

/**
 * Searchable dropdown for Sendit districts (/districts endpoint).
 * Shows district name + delivery price. Calls onChange with the selected
 * district's id, name and price so the caller can persist all three.
 *
 *   onChange({ id, name, price, raw })
 *
 * When the user types something that doesn't match any district, no
 * selection is committed — the input is purely a search box. If you need
 * free-text fallback for the recipient, set allowFreeText (the caller will
 * receive plain string typings via onFreeTextChange).
 */
export default function SenditDistrictPicker({
  value,           // selected id
  valueLabel,      // selected name (for display when only id is known)
  valuePrice,      // selected price (DH)
  placeholder = 'Choisir une ville…',
  disabled = false,
  allowFreeText = false,
  freeText = '',
  onChange,
  onFreeTextChange,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const wrapRef = useRef(null);
  const debounce = useRef(null);

  // Click-outside closes the menu
  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  // Debounced search
  useEffect(() => {
    if (!open) return;
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(async () => {
      try {
        setLoading(true);
        setError('');
        const r = await shipmentService.districts({
          search: query || undefined,
          page: 1,
        });
        // Sendit's exact shape isn't documented — handle the common variants.
        const raw = r?.data?.data || r?.data || r?.districts || r || [];
        const list = Array.isArray(raw) ? raw : (raw.data || []);
        setResults(list);
      } catch (e) {
        setError(e.response?.data?.message || 'Erreur lors du chargement des villes.');
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => debounce.current && clearTimeout(debounce.current);
  }, [query, open]);

  // Field extractors — Sendit responses use varying names
  const getId    = (c) => c.id ?? c.code ?? c.district_id ?? c.value;
  const getName  = (c) => c.name ?? c.city ?? c.label ?? c.district ?? c.title ?? '';
  const getPrice = (c) =>
    c.price ?? c.cost ?? c.delivery_price ?? c.shipping_price ?? c.tarif ?? c.tarif_livraison ?? c.amount ?? null;

  const fmtPrice = (p) =>
    p === null || p === undefined || p === '' ? null : `${parseFloat(p).toFixed(2)} DH`;

  // What to display in the input
  const displayValue = open
    ? query
    : (allowFreeText
        ? (freeText || valueLabel || '')
        : (valueLabel ? `${valueLabel}${valuePrice ? ` — ${fmtPrice(valuePrice)}` : ''}` : ''));

  const handleSelect = (c) => {
    const id    = getId(c);
    const name  = getName(c);
    const price = getPrice(c);
    onChange?.({
      id: id != null ? String(id) : null,
      name,
      price: price != null && price !== '' ? parseFloat(price) : null,
      raw: c,
    });
    if (allowFreeText) onFreeTextChange?.(name);
    setQuery('');
    setOpen(false);
  };

  const handleInput = (e) => {
    const v = e.target.value;
    setQuery(v);
    if (allowFreeText) onFreeTextChange?.(v);
    if (!open) setOpen(true);
  };

  const handleClear = () => {
    onChange?.({ id: null, name: '', price: null, raw: null });
    if (allowFreeText) onFreeTextChange?.('');
    setQuery('');
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <input
        type="text"
        value={displayValue}
        disabled={disabled}
        placeholder={placeholder}
        onChange={handleInput}
        onFocus={() => setOpen(true)}
        autoComplete="off"
        style={{
          width: '100%', padding: '8px 36px 8px 12px',
          border: '1px solid #ced4da', borderRadius: 6, fontSize: 14,
        }}
      />
      {value && !open && !disabled && (
        <button
          type="button"
          onClick={handleClear}
          title="Effacer la sélection"
          style={{
            position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)',
            background: 'none', border: 'none', cursor: 'pointer', color: '#999', padding: 4,
          }}
        >
          ×
        </button>
      )}

      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 2,
          background: '#fff', border: '1px solid #ced4da', borderRadius: 6,
          maxHeight: 280, overflowY: 'auto', zIndex: 10,
          boxShadow: '0 6px 18px rgba(0,0,0,0.12)',
        }}>
          {loading ? (
            <div style={{ padding: 12, fontSize: 13, color: '#666' }}>Recherche…</div>
          ) : error ? (
            <div style={{ padding: 12, fontSize: 13, color: '#b00020' }}>{error}</div>
          ) : results.length === 0 ? (
            <div style={{ padding: 12, fontSize: 13, color: '#666' }}>
              {query ? 'Aucune ville trouvée.' : 'Commencez à taper pour rechercher…'}
            </div>
          ) : (
            results.slice(0, 50).map((c, i) => {
              const id = getId(c);
              const name = getName(c);
              const price = getPrice(c);
              const isSelected = value && String(value) === String(id);
              return (
                <button
                  key={id ?? `${name}-${i}`}
                  type="button"
                  onMouseDown={(e) => { e.preventDefault(); handleSelect(c); }}
                  style={{
                    width: '100%', textAlign: 'left', padding: '10px 12px',
                    background: isSelected ? '#e7f1ff' : 'none', border: 'none',
                    borderBottom: '1px solid #f1f3f5', cursor: 'pointer',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    fontSize: 13, gap: 12,
                  }}
                  onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = '#f8f9fa'; }}
                  onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = 'none'; }}
                >
                  <span style={{ fontWeight: isSelected ? 600 : 400 }}>{name}</span>
                  {fmtPrice(price) && (
                    <span style={{
                      fontFamily: 'monospace', fontSize: 12,
                      color: isSelected ? '#0a3a82' : '#0d6efd',
                      whiteSpace: 'nowrap',
                    }}>
                      {fmtPrice(price)}
                    </span>
                  )}
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
